import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Bottom-sheet presentation for phone-width floating surfaces.
 *
 * A 390px screen has no room for a popover anchored beside its trigger, so on a phone
 * every floating surface docks to the bottom edge at full width instead.
 *
 * Two flavours, because the surfaces differ in how they are placed:
 *
 * - `sheetDocked` positions as well as shapes. Anchored popovers pick it *instead of*
 *   their desktop classes (`useIsPhone`) rather than layering `max-sm:` overrides on
 *   top — `max-sm:top-auto` and `top-full` set the same property, and which one wins
 *   comes down to stylesheet order, which is not something to build on.
 * - `sheetSurface` only shapes, for surfaces already positioned by something that wins
 *   outright: the Modal's own flex overlay, or the word popovers' inline `style`.
 *
 * Pair either with `useSheetAnchor`, which keeps the trigger visible above the sheet.
 */

/** Shape only: full width, top corners, no side borders. Safe to layer with `max-sm:`. */
export const sheetSurface = [
  'max-sm:w-full max-sm:max-w-none max-sm:mt-0',
  'max-sm:rounded-2xl max-sm:rounded-b-none',
  'max-sm:border-x-0 max-sm:border-b-0',
  'max-sm:max-h-[80dvh] max-sm:overflow-y-auto',
  'max-sm:shadow-[0_-14px_44px_-16px_rgba(0,0,0,0.45)]',
].join(' ')

/** Shape and position, for a surface that would otherwise anchor to its trigger. */
export const sheetDocked = [
  'fixed inset-x-0 bottom-0 z-50 w-full',
  'rounded-t-2xl border-t border-border-divider bg-card',
  'max-h-[80dvh] overflow-y-auto',
  'shadow-[0_-14px_44px_-16px_rgba(0,0,0,0.45)]',
].join(' ')

/** Keeps a sheet's last row clear of the home indicator. */
export const sheetSafeArea = 'max-sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]'

/**
 * Renders a docked sheet at the end of `<body>`.
 *
 * `position: fixed` is measured against the nearest ancestor with a transform, not the
 * viewport — and the page entrance animations put a transform on half the wrappers in
 * the app. A sheet left in place lands wherever that ancestor happens to be rather than
 * on the bottom edge, so it moves to the body instead. Pair with `useDismiss`'s
 * `alsoInside`, since the sheet is no longer a descendant of its trigger.
 */
export function SheetPortal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') return null
  return createPortal(children, document.body)
}

/** Dimmer behind a docked sheet, so the page behind it reads as out of reach. */
export function SheetScrim({ onClick }: { onClick?: () => void }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]"
      onClick={onClick}
      aria-hidden="true"
    />
  )
}

/**
 * Wraps a popover that is a sheet on phones and an anchored panel elsewhere: portals
 * and dims it in the first case, and gets out of the way in the second.
 */
export function SheetWrapper(
  { isPhone, onScrimClick, children }: { isPhone: boolean; onScrimClick: () => void; children: ReactNode },
) {
  if (!isPhone) return <>{children}</>
  return (
    <SheetPortal>
      <SheetScrim onClick={onScrimClick} />
      {children}
    </SheetPortal>
  )
}

/** The grab bar that tells a phone user this panel belongs to the bottom edge. */
export function SheetHandle() {
  return (
    <div className="flex justify-center pt-2.5 pb-1 sm:hidden" aria-hidden="true">
      <span className="h-1 w-9 rounded-full bg-muted/40" />
    </div>
  )
}
